/*
 ************************************************************************
 *  © [2015 - 2023] Quintype Technologies India Private Limited
 *  All Rights Reserved.
 *************************************************************************
 */

import React from "react";
import { connect } from "react-redux";
import { AnyAction } from "redux";
import { ThunkDispatch } from "redux-thunk";

import { LoaderState } from "behaviors/loader/state";
import { PartialAppState } from "../custom-fields/state";
import { AllowedFieldTypes, CustomField, UnsavedCustomField } from "api/custom-field";
import {
  loadCustomFields,
  saveCustomField,
  deleteCustomField,
  SuccessFn,
  FailureFn
} from "../custom-fields/async-action-creators";
import { navigate } from "utils/routes.utils";

import Inspector from "./components/inspector";
import LoaderWrapper from "behaviors/loader/components/loader-wrapper/loader-wrapper";
import Loader from "./loader";
import CustomMetadataTree from "./components/custom-metadata-tree";
import { CUSTOM_METADATA_INDEX_PATH, CUSTOM_METADATA_NEW_PATH } from "./routes";
import { NOTIFICATION_SUCCESS, NOTIFICATION_ERROR } from "containers/page/actions";
import { t } from "i18n";

interface StateProps {
  main: LoaderState;
  customMetadataFields: Array<CustomField>;
  save: LoaderState | null;
  deleteState: LoaderState | null; //named as deleteState since delete is a Javascript keyword
}

interface DispatchProps {
  loadCustomMetadataFields: () => void;
  openAddInspector: () => void;
  saveCustomMetadataField: (customField: UnsavedCustomField) => void;
  cancelSave: () => void;
  deleteCustomMetadataField: (customField: CustomField) => void;
}

interface OwnProps {
  shouldOpenAddInspector?: boolean;
}

type Props = OwnProps & StateProps & DispatchProps;

export const CustomMetadata: React.FC<Props> = ({
  main,
  customMetadataFields,
  loadCustomMetadataFields,
  saveCustomMetadataField,
  cancelSave,
  deleteCustomMetadataField,
  shouldOpenAddInspector = false,
  openAddInspector
}) => {
  return (
    <React.Fragment>
      <section className="page-container">
        <LoaderWrapper
          className={"metadata-progress-main-area"}
          component={Loader}
          loader={main}
          loadCustomMetadata={() => loadCustomMetadataFields()}>
          <CustomMetadataTree
            customMetadataFields={customMetadataFields}
            deleteCustomMetadataField={deleteCustomMetadataField}
            openAddInspector={openAddInspector}
          />
          <Inspector
            active={shouldOpenAddInspector}
            saveCustomMetadataField={saveCustomMetadataField}
            cancelSave={cancelSave}
          />
        </LoaderWrapper>
      </section>
    </React.Fragment>
  );
};

const mapStateToProps = (state: PartialAppState) => ({
  main: state.customFields.ui.main,
  save: state.customFields.ui.save,
  deleteState: state.customFields.ui.delete,
  customMetadataFields: state.customFields.customFields
});

const saveSuccessFn: SuccessFn = (dispatch) => {
  dispatch({
    type: NOTIFICATION_SUCCESS,
    payload: {
      message: t("custom-metadata.messages.save-success")
    }
  });

  dispatch(navigate(CUSTOM_METADATA_INDEX_PATH, {}, { refresh: true }));
};

const saveFailureFn: FailureFn = (e, dispatch) => {
  const errorInfo = JSON.parse(e.message);

  if (errorInfo.status && errorInfo.status === 409) {
    dispatch({
      type: NOTIFICATION_ERROR,
      payload: {
        message: t("custom-metadata.messages.validations.duplicate-name")
      }
    });
  } else {
    dispatch({
      type: NOTIFICATION_ERROR,
      payload: {
        message: t("custom-metadata.messages.unknown-error")
      }
    });
  }
};

const deleteSuccessFn: SuccessFn = (dispatch) => {
  dispatch({
    type: NOTIFICATION_SUCCESS,
    payload: {
      message: t("custom-metadata.messages.delete-success")
    }
  });

  dispatch(navigate(CUSTOM_METADATA_INDEX_PATH, {}, { refresh: true }));
};

const deleteFailureFn: FailureFn = (_, dispatch) => {
  dispatch({
    type: NOTIFICATION_ERROR,
    payload: {
      message: t("custom-metadata.messages.delete-failure")
    }
  });
};

const mapDispatchToProps = (dispatch: ThunkDispatch<PartialAppState, void, AnyAction>) => ({
  loadCustomMetadataFields: () => dispatch(loadCustomFields({ "field-type": AllowedFieldTypes.METADATA })),
  openAddInspector: () => dispatch(navigate(CUSTOM_METADATA_NEW_PATH)),
  saveCustomMetadataField: (customField: UnsavedCustomField) =>
    dispatch(saveCustomField(customField, saveSuccessFn, saveFailureFn)),
  deleteCustomMetadataField: (customField: CustomField) =>
    dispatch(deleteCustomField(customField, deleteSuccessFn, deleteFailureFn)),
  cancelSave: () => dispatch(navigate(CUSTOM_METADATA_INDEX_PATH))
});

export default connect(mapStateToProps, mapDispatchToProps)(CustomMetadata);
